import React, { useEffect, useState } from "react";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  useGetDealerMoveOptionsMutation,
  useMoveDeviceToDealerCustomerMutation,
  useTakeBackDeviceToDealerMutation,
} from "../../../store/storageRoles/devicesApi";
import { getDeviceDisplayName } from "../../../utils/deviceUtils";
import OperationDrawer from "../OperationDrawer";

/**
 * Merkezden bayi adına taşıma (#329, sözleşme dealer-device-moves.md v1.1).
 *
 * mode "toCustomer" → Bayi Müşterisine Ver: bayinin elindeki cihaz bayinin
 * ağındaki müşteriye. mode "takeBack" → Bayiye Geri Al: bayiye bağlı
 * müşterideki cihaz bayinin deposuna. Seçim listesi açılışta sunucudan gelir
 * (dealerMoveOptions); işlem bayi panelindeki Alt Firmaya Ver / Bayiye Geri
 * Al'ın kendisidir. Ana Cihazlar listesi ve firma Cihazlar sekmesi kullanır.
 */
export const CENTER_DEALER_MOVE_LABELS = {
  toCustomer: "Bayi Müşterisine Ver",
  takeBack: "Bayiye Geri Al",
};

function CenterDealerMoveDrawer({ open, device, mode, onClose, onDone }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();
  const [options, setOptions] = useState(null);

  const [getOptions] = useGetDealerMoveOptionsMutation();
  const [toCustomer, toCustomerStatus] = useMoveDeviceToDealerCustomerMutation();
  const [takeBack, takeBackStatus] = useTakeBackDeviceToDealerMutation();
  const status = mode === "takeBack" ? takeBackStatus : toCustomerStatus;

  // Açılışta seçenekler — cihaz bu işleme uygun değilse kapatılır.
  useEffect(() => {
    if (!open || !device?._id) return;
    let cancelled = false;
    setOptions(null);
    toCustomerStatus.reset();
    takeBackStatus.reset();
    getOptions({ token, id: device._id })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        const section = res?.data?.[mode];
        if (!section) {
          showError(`Bu cihaz için ${CENTER_DEALER_MOVE_LABELS[mode]} yapılamaz.`);
          onClose();
          return;
        }
        setOptions(section);
      })
      .catch((err) => {
        if (cancelled) return;
        showError(err?.data?.message || "Seçenekler alınamadı.");
        onClose();
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?._id, mode]);

  useEffect(() => {
    if (!status.isSuccess && !status.isError) return;
    const name = getDeviceDisplayName(device);
    const label = CENTER_DEALER_MOVE_LABELS[mode];
    if (status.isSuccess) {
      showSuccess(`"${name}" (${device?.serialNo}) için ${label} işlemi tamamlandı.`);
      if (onDone && status.data?.data?.device) onDone(status.data.data.device);
    } else {
      showError(`"${name}" (${device?.serialNo}) - ${status.error?.data?.message || "İşlem sırasında bir hata oluştu."}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.isSuccess, status.isError]);

  const handleConfirm = ({ connect, description, firmId, isActive, dealerFirmId }) => {
    if (mode === "toCustomer") {
      if (!firmId) return;
      toCustomer({ token, id: device._id, firmId, connect, description: description || "", isActive: !!isActive });
    } else {
      takeBack({ token, id: device._id, dealerFirmId, connect, description: description || "" });
    }
  };

  const firms = mode === "toCustomer" ? options?.firms || [] : options?.dealerFirms || [];
  const dealerName =
    mode === "toCustomer"
      ? options?.dealerFirm?.firmName || ""
      : firms.length === 1
      ? firms[0].firmName
      : "";

  return (
    <OperationDrawer
      open={open}
      device={device}
      operation={mode === "toCustomer" ? "Firma" : "Depo"}
      centerDealerMove={mode}
      dealerName={dealerName}
      firms={firms}
      onClose={onClose}
      onConfirm={handleConfirm}
      isLoading={status.isLoading}
      isSuccess={status.isSuccess}
      isError={status.isError}
      errorMessage={status.error?.data?.message || ""}
    />
  );
}

export default CenterDealerMoveDrawer;
